import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import clsx from 'clsx'
import { api, apiErrorMessage } from '../../services/api'
import { useAuthStore } from '../../context/auth'
import { Badge, Button, Card, ErrorText, Field, Input, Modal, Select, Spinner, Table, EmptyState, Textarea } from '../../components/ui'
import {
  clp,
  formatDateShort,
  HEALTH_ROLE_LABELS,
  SERVICE_CATEGORY_LABELS,
  USER_ROLE_LABELS,
} from '../../utils/format'
import type { Company, CompanyContact, FeeRule, NotificationTemplate, Professional, Service, UserAccount } from '../../types/api'

type Tab = 'profesionales' | 'prestaciones' | 'empresas' | 'honorarios' | 'plantillas' | 'usuarios'

const TABS: Array<{ id: Tab; label: string; adminOnly?: boolean }> = [
  { id: 'profesionales', label: 'Profesionales' },
  { id: 'prestaciones', label: 'Prestaciones' },
  { id: 'empresas', label: 'Empresas y convenios' },
  { id: 'honorarios', label: 'Reglas de honorarios' },
  { id: 'plantillas', label: 'Plantillas de notificación', adminOnly: true },
  { id: 'usuarios', label: 'Usuarios', adminOnly: true },
]

export function MantenedoresPage() {
  const user = useAuthStore((s) => s.user)
  const isAdmin = user?.role === 'ADMIN'
  const tabs = TABS.filter((t) => !t.adminOnly || isAdmin)
  const [tab, setTab] = useState<Tab>('profesionales')

  return (
    <div className="space-y-4">
      <h1 className="font-display text-2xl font-bold text-ink">Mantenedores</h1>
      <div className="flex flex-wrap gap-1.5">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={clsx(
              'rounded-lg px-3 py-1.5 text-sm font-medium transition-colors',
              tab === t.id ? 'bg-brand-900 text-white' : 'bg-white text-gray-600 hover:bg-soft',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>
      {tab === 'profesionales' && <ProfesionalesTab isAdmin={isAdmin} />}
      {tab === 'prestaciones' && <PrestacionesTab isAdmin={isAdmin} />}
      {tab === 'empresas' && <EmpresasTab isAdmin={isAdmin} />}
      {tab === 'honorarios' && <ReglasTab />}
      {tab === 'plantillas' && isAdmin && <PlantillasTab />}
      {tab === 'usuarios' && isAdmin && <UsuariosTab />}
    </div>
  )
}

// ---------- Profesionales ----------

function ProfesionalesTab({ isAdmin }: { isAdmin: boolean }) {
  const queryClient = useQueryClient()
  const [error, setError] = useState('')
  const [form, setForm] = useState({ firstName: '', lastName: '', rut: '', healthRole: 'PHYSICIAN', specialty: '', registryNumber: '' })

  const professionals = useQuery({
    queryKey: ['professionals'],
    queryFn: async () => (await api.get<Professional[]>('/professionals')).data,
  })

  const create = useMutation({
    mutationFn: async () => (await api.post('/professionals', { ...form, rut: form.rut || null })).data,
    onSuccess: () => {
      setForm({ firstName: '', lastName: '', rut: '', healthRole: 'PHYSICIAN', specialty: '', registryNumber: '' })
      setError('')
      queryClient.invalidateQueries({ queryKey: ['professionals'] })
    },
    onError: (err) => setError(apiErrorMessage(err)),
  })

  /**
   * Sobrecupo automático por profesional (DT-62). Apagado por defecto: apretar
   * la agenda de alguien es una decisión suya, no un valor del sistema.
   */
  const setOverbooking = useMutation({
    mutationFn: async ({ id, minutes }: { id: string; minutes: number }) =>
      (
        await api.patch(`/professionals/${id}`, {
          overbookingEnabled: minutes > 0,
          ...(minutes > 0 ? { overbookingSlotMin: minutes } : {}),
        })
      ).data,
    onSuccess: () => {
      setError('')
      queryClient.invalidateQueries({ queryKey: ['professionals'] })
    },
    onError: (err) => setError(apiErrorMessage(err)),
  })

  return (
    <div className="space-y-4">
      <Card title="Equipo profesional">
        {professionals.isLoading ? (
          <Spinner />
        ) : (
          <>
            <Table headers={['Nombre', 'Rol de salud', 'Especialidad', 'Registro', 'Sobrecupo', 'Estado']}>
              {(professionals.data ?? []).map((p) => (
                <tr key={p.id}>
                  <td className="px-2 py-2 font-medium">{p.firstName} {p.lastName}</td>
                  <td className="px-2 py-2">{HEALTH_ROLE_LABELS[p.healthRole]}</td>
                  <td className="px-2 py-2 text-gray-600">{p.specialty ?? '—'}</td>
                  <td className="px-2 py-2 text-xs text-gray-500">{p.registryNumber ?? '—'}</td>
                  <td className="px-2 py-2">
                    {isAdmin ? (
                      <Select
                        aria-label={`Sobrecupo de ${p.firstName} ${p.lastName}`}
                        className="w-28 text-xs"
                        value={p.overbookingEnabled ? String(p.overbookingSlotMin ?? 15) : '0'}
                        onChange={(e) => setOverbooking.mutate({ id: p.id, minutes: Number(e.target.value) })}
                      >
                        <option value="0">Apagado</option>
                        {[10, 15, 20, 30].map((m) => (
                          <option key={m} value={m}>{m} min</option>
                        ))}
                      </Select>
                    ) : (
                      <span className="text-xs text-gray-500">
                        {p.overbookingEnabled ? `${p.overbookingSlotMin ?? 15} min` : 'Apagado'}
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-2">
                    <Badge className={p.isActive ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-200 text-gray-600'}>
                      {p.isActive ? 'Activo' : 'Inactivo'}
                    </Badge>
                  </td>
                </tr>
              ))}
            </Table>
            {/* Qué significa la columna: el sobrecupo es automático, no un botón
                que alguien tenga que apretar el día que la agenda se llena. */}
            <p className="mt-2 text-xs text-gray-500">
              Con sobrecupo activo, cuando un profesional copa todas las horas de un día la web abre sola una segunda
              tanda en los intermedios (09:15, 09:45…) con la duración elegida.
            </p>
          </>
        )}
      </Card>
      {isAdmin && (
        <Card title="Agregar profesional">
          <div className="grid items-end gap-3 sm:grid-cols-3 lg:grid-cols-6">
            <Field label="Nombres"><Input value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} /></Field>
            <Field label="Apellidos"><Input value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} /></Field>
            <Field label="RUT"><Input value={form.rut} onChange={(e) => setForm({ ...form, rut: e.target.value })} /></Field>
            <Field label="Rol de salud">
              <Select value={form.healthRole} onChange={(e) => setForm({ ...form, healthRole: e.target.value })}>
                {Object.entries(HEALTH_ROLE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </Select>
            </Field>
            <Field label="Especialidad"><Input value={form.specialty} onChange={(e) => setForm({ ...form, specialty: e.target.value })} /></Field>
            <Button disabled={!form.firstName || !form.lastName || create.isPending} onClick={() => create.mutate()}>Agregar</Button>
          </div>
          <div className="mt-2"><ErrorText>{error}</ErrorText></div>
        </Card>
      )}
    </div>
  )
}

// ---------- Prestaciones ----------

function PrestacionesTab({ isAdmin }: { isAdmin: boolean }) {
  const queryClient = useQueryClient()
  const [error, setError] = useState('')
  const EMPTY_SERVICE = {
    code: '',
    name: '',
    category: 'CONSULTATION',
    durationMin: '30',
    basePrice: '',
    cancellationWindowHours: '24',
    fonasaCode: '',
    schedulingMode: 'PROFESSIONAL',
    bookableOnline: false,
    requiresMedicalOrder: false,
  }
  const [form, setForm] = useState(EMPTY_SERVICE)

  const services = useQuery({
    queryKey: ['services'],
    queryFn: async () => (await api.get<Service[]>('/catalog/services')).data,
  })

  const create = useMutation({
    mutationFn: async () =>
      (
        await api.post('/catalog/services', {
          code: form.code.toUpperCase(),
          name: form.name,
          category: form.category,
          durationMin: Number(form.durationMin),
          basePrice: Number(form.basePrice),
          cancellationWindowHours: Number(form.cancellationWindowHours),
          fonasaCode: form.fonasaCode.trim() || null,
          schedulingMode: form.schedulingMode,
          bookableOnline: form.bookableOnline,
          requiresMedicalOrder: form.requiresMedicalOrder,
        })
      ).data,
    onSuccess: () => {
      setForm(EMPTY_SERVICE)
      setError('')
      queryClient.invalidateQueries({ queryKey: ['services'] })
    },
    onError: (err) => setError(apiErrorMessage(err)),
  })

  return (
    <div className="space-y-4">
      <Card title="Catálogo de prestaciones">
        {services.isLoading ? (
          <Spinner />
        ) : (
          <Table headers={['Código', 'Nombre', 'Categoría', 'Código Fonasa', 'Agenda', 'Duración', 'Precio', 'Abono']}>
            {(services.data ?? []).map((s) => (
              <tr key={s.id}>
                <td className="px-2 py-2 font-mono text-xs">{s.code}</td>
                <td className="px-2 py-2 font-medium">
                  {s.name}
                  {s.bookableOnline && <Badge className="ml-1.5 bg-brand-100 text-brand-700">Web</Badge>}
                  {s.requiresMedicalOrder && <Badge className="ml-1.5 bg-amber-100 text-amber-800">Orden</Badge>}
                </td>
                <td className="px-2 py-2"><Badge>{SERVICE_CATEGORY_LABELS[s.category]}</Badge></td>
                {/* TODO-NEGOCIO: los códigos del arancel los carga la clínica. */}
                <td className="px-2 py-2 font-mono text-xs text-gray-600">{s.fonasaCode ?? '—'}</td>
                <td className="px-2 py-2 text-xs text-gray-500">
                  {s.schedulingMode === 'RESOURCE' ? 'Sala o equipo' : 'Profesional'}
                </td>
                <td className="px-2 py-2">{s.durationMin} min</td>
                <td className="px-2 py-2">{clp(s.basePrice)}</td>
                <td className="px-2 py-2 text-xs text-gray-500">
                  {s.requiresDeposit ? (s.depositPercent ? `${s.depositPercent}%` : clp(s.depositAmount ?? 0)) : '—'}
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Card>
      {isAdmin && (
        <Card title="Agregar prestación">
          <div className="grid items-end gap-3 sm:grid-cols-3 lg:grid-cols-7">
            <Field label="Código"><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="CONS-X" /></Field>
            <Field label="Nombre"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
            <Field label="Categoría">
              <Select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                {Object.entries(SERVICE_CATEGORY_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </Select>
            </Field>
            <Field label="Duración (min)"><Input type="number" value={form.durationMin} onChange={(e) => setForm({ ...form, durationMin: e.target.value })} /></Field>
            <Field label="Precio CLP"><Input type="number" value={form.basePrice} onChange={(e) => setForm({ ...form, basePrice: e.target.value })} /></Field>
            <Field label="Ventana anulación (h)"><Input type="number" value={form.cancellationWindowHours} onChange={(e) => setForm({ ...form, cancellationWindowHours: e.target.value })} /></Field>
            <Field label="Código Fonasa" hint="Arancel MAI/MLE">
              <Input value={form.fonasaCode} onChange={(e) => setForm({ ...form, fonasaCode: e.target.value })} placeholder="04-01-001" />
            </Field>
            <Field label="Quién agenda la hora">
              <Select value={form.schedulingMode} onChange={(e) => setForm({ ...form, schedulingMode: e.target.value })}>
                <option value="PROFESSIONAL">El profesional (consultas)</option>
                <option value="RESOURCE">La sala o equipo (exámenes)</option>
              </Select>
            </Field>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={form.bookableOnline}
                onChange={(e) => setForm({ ...form, bookableOnline: e.target.checked })}
                className="h-4 w-4 rounded border-steel-300 text-brand-600"
              />
              Reservable en la web
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={form.requiresMedicalOrder}
                onChange={(e) => setForm({ ...form, requiresMedicalOrder: e.target.checked })}
                className="h-4 w-4 rounded border-steel-300 text-brand-600"
              />
              Exige orden médica
            </label>
            <Button disabled={!form.code || !form.name || !form.basePrice || create.isPending} onClick={() => create.mutate()}>Agregar</Button>
          </div>
          {form.schedulingMode === 'RESOURCE' && (
            <p className="mt-2 text-xs text-gray-500">
              Una prestación agendada por sala necesita además su sala asignada y el horario de esa sala; mientras no
              los tenga, no ofrece horas.
            </p>
          )}
          <div className="mt-2"><ErrorText>{error}</ErrorText></div>
        </Card>
      )}
    </div>
  )
}

// ---------- Empresas y convenios ----------

function EmpresasTab({ isAdmin }: { isAdmin: boolean }) {
  const queryClient = useQueryClient()
  const [error, setError] = useState('')
  const [form, setForm] = useState({ rut: '', businessName: '', contactName: '', email: '', phone: '' })
  const [contactsFor, setContactsFor] = useState<Company | null>(null)

  const companies = useQuery({
    queryKey: ['companies'],
    queryFn: async () => (await api.get<Company[]>('/companies')).data,
  })

  const create = useMutation({
    mutationFn: async () => (await api.post('/companies', form)).data,
    onSuccess: () => {
      setForm({ rut: '', businessName: '', contactName: '', email: '', phone: '' })
      setError('')
      queryClient.invalidateQueries({ queryKey: ['companies'] })
    },
    onError: (err) => setError(apiErrorMessage(err)),
  })

  return (
    <div className="space-y-4">
      <Card title="Empresas cliente">
        {companies.isLoading ? (
          <Spinner />
        ) : (companies.data?.length ?? 0) === 0 ? (
          <EmptyState message="Sin empresas registradas." />
        ) : (
          <div className="space-y-3">
            {(companies.data ?? []).map((c) => (
              <div key={c.id} className="rounded-lg border border-gray-200 p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="font-semibold text-ink">{c.businessName}</p>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">{c.rut} · {c.contactName ?? ''} {c.email ? `· ${c.email}` : ''}</span>
                    {isAdmin && (
                      <Button variant="secondary" onClick={() => setContactsFor(c)}>Portal: contactos</Button>
                    )}
                  </div>
                </div>
                {c.agreements.length > 0 ? (
                  <ul className="mt-2 space-y-1 text-sm">
                    {c.agreements.map((a) => (
                      <li key={a.id} className="rounded bg-soft px-2.5 py-1.5">
                        <span className="font-medium">{a.name}</span>{' '}
                        <span className="text-xs text-gray-500">
                          ({formatDateShort(a.validFrom)}{a.validTo ? ` → ${formatDateShort(a.validTo)}` : ''})
                        </span>
                        {a.rates.length > 0 && (
                          <span className="block text-xs text-gray-600">
                            Tarifas: {a.rates.map((r) => `${r.battery?.name ?? r.service?.name}: ${clp(r.price)}`).join(' · ')}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="mt-1 text-xs text-gray-400">Sin convenios.</p>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>
      <Card title="Agregar empresa">
        <div className="grid items-end gap-3 sm:grid-cols-3 lg:grid-cols-6">
          <Field label="RUT empresa"><Input value={form.rut} onChange={(e) => setForm({ ...form, rut: e.target.value })} placeholder="76.543.210-3" /></Field>
          <Field label="Razón social"><Input value={form.businessName} onChange={(e) => setForm({ ...form, businessName: e.target.value })} /></Field>
          <Field label="Contacto"><Input value={form.contactName} onChange={(e) => setForm({ ...form, contactName: e.target.value })} /></Field>
          <Field label="Email"><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
          <Field label="Teléfono"><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
          <Button disabled={!form.rut || !form.businessName || create.isPending} onClick={() => create.mutate()}>Agregar</Button>
        </div>
        <div className="mt-2"><ErrorText>{error}</ErrorText></div>
      </Card>
      {contactsFor && <ContactsModal company={contactsFor} onClose={() => setContactsFor(null)} />}
    </div>
  )
}

/** Contactos del portal de empresa: invitar, reinvitar y activar/desactivar. */
function ContactsModal({ company, onClose }: { company: Company; onClose: () => void }) {
  const queryClient = useQueryClient()
  const [error, setError] = useState('')
  const [form, setForm] = useState({ email: '', firstName: '', lastName: '' })
  const [inviteUrl, setInviteUrl] = useState('')

  const contacts = useQuery({
    queryKey: ['company-contacts', company.id],
    queryFn: async () => (await api.get<CompanyContact[]>(`/companies/${company.id}/contacts`)).data,
  })

  const invite = useMutation({
    mutationFn: async () => (await api.post(`/companies/${company.id}/contacts`, form)).data,
    onSuccess: (data: { inviteUrl: string }) => {
      setForm({ email: '', firstName: '', lastName: '' })
      setError('')
      setInviteUrl(data.inviteUrl)
      queryClient.invalidateQueries({ queryKey: ['company-contacts', company.id] })
    },
    onError: (err) => setError(apiErrorMessage(err)),
  })

  const reinvite = useMutation({
    mutationFn: async (userId: string) => (await api.post(`/companies/contacts/${userId}/reinvite`)).data,
    onSuccess: (data: { inviteUrl: string }) => setInviteUrl(data.inviteUrl),
    onError: (err) => setError(apiErrorMessage(err)),
  })

  const toggle = useMutation({
    mutationFn: async ({ userId, isActive }: { userId: string; isActive: boolean }) =>
      (await api.patch(`/companies/contacts/${userId}`, { isActive })).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['company-contacts', company.id] }),
    onError: (err) => setError(apiErrorMessage(err)),
  })

  return (
    <Modal title={`Contactos del portal — ${company.businessName}`} onClose={onClose} wide>
      <div className="space-y-3">
        <p className="text-sm text-gray-600">
          Los contactos acceden al portal de empresa (<span className="font-mono text-xs">/empresa</span>) para cotizar, seguir órdenes y ver su facturación.
        </p>
        {contacts.isLoading ? (
          <Spinner />
        ) : (contacts.data?.length ?? 0) === 0 ? (
          <EmptyState message="Sin contactos invitados." />
        ) : (
          <Table headers={['Nombre', 'Email', 'Estado', '']}>
            {(contacts.data ?? []).map((c) => (
              <tr key={c.id}>
                <td className="px-2 py-2 font-medium">{c.firstName} {c.lastName}</td>
                <td className="px-2 py-2 text-gray-600">{c.email}</td>
                <td className="px-2 py-2">
                  <Badge className={c.isActive ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-200 text-gray-600'}>
                    {c.isActive ? 'Activo' : 'Desactivado'}
                  </Badge>
                </td>
                <td className="px-2 py-2">
                  <div className="flex gap-1">
                    {c.isActive && (
                      <Button variant="ghost" disabled={reinvite.isPending} onClick={() => reinvite.mutate(c.id)}>
                        Reinvitar
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      disabled={toggle.isPending}
                      onClick={() => toggle.mutate({ userId: c.id, isActive: !c.isActive })}
                    >
                      {c.isActive ? 'Desactivar' : 'Reactivar'}
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </Table>
        )}

        {inviteUrl && (
          <div className="rounded-lg bg-soft p-3 text-sm">
            <p className="font-medium text-ink">Enlace de activación (también se envió por correo):</p>
            <p className="mt-1 break-all font-mono text-xs text-gray-600">{inviteUrl}</p>
          </div>
        )}

        <div className="grid items-end gap-3 sm:grid-cols-4">
          <Field label="Nombres"><Input value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} /></Field>
          <Field label="Apellidos"><Input value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} /></Field>
          <Field label="Email"><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
          <Button
            variant="cta"
            disabled={!form.email || !form.firstName || !form.lastName || invite.isPending}
            onClick={() => invite.mutate()}
          >
            Invitar contacto
          </Button>
        </div>
        <ErrorText>{error}</ErrorText>
      </div>
    </Modal>
  )
}

// ---------- Reglas de honorarios ----------

function ReglasTab() {
  const queryClient = useQueryClient()
  const user = useAuthStore((s) => s.user)
  const canWrite = user?.role === 'ACCOUNTANT'
  const [error, setError] = useState('')
  const [form, setForm] = useState({ professionalId: '', healthRole: '', serviceId: '', serviceCategory: '', mode: 'PERCENTAGE', value: '' })

  const rules = useQuery({
    queryKey: ['fee-rules'],
    queryFn: async () => (await api.get<FeeRule[]>('/accounting/fee-rules')).data,
  })
  const professionals = useQuery({
    queryKey: ['professionals'],
    queryFn: async () => (await api.get<Professional[]>('/professionals')).data,
  })
  const services = useQuery({
    queryKey: ['services'],
    queryFn: async () => (await api.get<Service[]>('/catalog/services')).data,
  })

  const create = useMutation({
    mutationFn: async () =>
      (
        await api.post('/accounting/fee-rules', {
          professionalId: form.professionalId || null,
          healthRole: form.healthRole || null,
          serviceId: form.serviceId || null,
          serviceCategory: form.serviceCategory || null,
          mode: form.mode,
          value: Number(form.value),
        })
      ).data,
    onSuccess: () => {
      setForm({ professionalId: '', healthRole: '', serviceId: '', serviceCategory: '', mode: 'PERCENTAGE', value: '' })
      setError('')
      queryClient.invalidateQueries({ queryKey: ['fee-rules'] })
    },
    onError: (err) => setError(apiErrorMessage(err)),
  })

  return (
    <div className="space-y-4">
      <Card title="Reglas vigentes" actions={<span className="text-xs text-gray-400">Resolución: profesional &gt; prestación &gt; categoría &gt; rol</span>}>
        {rules.isLoading ? (
          <Spinner />
        ) : (
          <Table headers={['Aplica a', 'Prestación / categoría', 'Modalidad', 'Vigencia', 'Nota']}>
            {(rules.data ?? []).map((r) => (
              <tr key={r.id}>
                <td className="px-2 py-2 font-medium">
                  {r.professional ? `${r.professional.firstName} ${r.professional.lastName}` : r.healthRole ? HEALTH_ROLE_LABELS[r.healthRole] : 'Todos'}
                </td>
                <td className="px-2 py-2">
                  {r.service?.name ?? (r.serviceCategory ? SERVICE_CATEGORY_LABELS[r.serviceCategory] : 'Todas')}
                </td>
                <td className="px-2 py-2 font-medium text-ink">
                  {r.mode === 'PERCENTAGE' ? `${r.value}%` : clp(r.value)}
                </td>
                <td className="px-2 py-2 text-xs text-gray-500">
                  {formatDateShort(r.validFrom)}{r.validTo ? ` → ${formatDateShort(r.validTo)}` : ' →'}
                </td>
                <td className="px-2 py-2 text-xs text-gray-500">{r.notes ?? '—'}</td>
              </tr>
            ))}
          </Table>
        )}
      </Card>
      {canWrite && (
        <Card title="Nueva regla">
          <div className="grid items-end gap-3 sm:grid-cols-3 lg:grid-cols-7">
            <Field label="Profesional" hint="o deja vacío y usa rol">
              <Select value={form.professionalId} onChange={(e) => setForm({ ...form, professionalId: e.target.value })}>
                <option value="">—</option>
                {(professionals.data ?? []).map((p) => <option key={p.id} value={p.id}>{p.firstName} {p.lastName}</option>)}
              </Select>
            </Field>
            <Field label="Rol de salud">
              <Select value={form.healthRole} onChange={(e) => setForm({ ...form, healthRole: e.target.value })}>
                <option value="">—</option>
                {Object.entries(HEALTH_ROLE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </Select>
            </Field>
            <Field label="Prestación">
              <Select value={form.serviceId} onChange={(e) => setForm({ ...form, serviceId: e.target.value })}>
                <option value="">—</option>
                {(services.data ?? []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </Select>
            </Field>
            <Field label="Categoría">
              <Select value={form.serviceCategory} onChange={(e) => setForm({ ...form, serviceCategory: e.target.value })}>
                <option value="">—</option>
                {Object.entries(SERVICE_CATEGORY_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </Select>
            </Field>
            <Field label="Modalidad">
              <Select value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })}>
                <option value="PERCENTAGE">Porcentaje</option>
                <option value="FIXED">Monto fijo</option>
              </Select>
            </Field>
            <Field label={form.mode === 'PERCENTAGE' ? '%' : 'CLP'}>
              <Input type="number" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} />
            </Field>
            <Button disabled={(!form.professionalId && !form.healthRole) || !form.value || create.isPending} onClick={() => create.mutate()}>
              Crear
            </Button>
          </div>
          <div className="mt-2"><ErrorText>{error}</ErrorText></div>
        </Card>
      )}
      {!canWrite && <p className="text-xs text-gray-400">Solo Contabilidad crea o modifica reglas de honorarios.</p>}
    </div>
  )
}

// ---------- Plantillas de notificación ----------

const EVENT_LABELS: Record<string, string> = {
  APPOINTMENT_CREATED: 'Cita creada',
  REMINDER_24H: 'Recordatorio 24 h',
  CONFIRMATION_REQUEST: 'Solicitud de confirmación',
  APPOINTMENT_CANCELLED: 'Cita anulada',
  APPOINTMENT_RESCHEDULED: 'Cita reagendada',
}

function PlantillasTab() {
  const queryClient = useQueryClient()
  const [error, setError] = useState('')
  const [editing, setEditing] = useState<NotificationTemplate | null>(null)
  const [body, setBody] = useState('')

  const templates = useQuery({
    queryKey: ['templates'],
    queryFn: async () => (await api.get<NotificationTemplate[]>('/notifications/templates')).data,
  })

  const save = useMutation({
    mutationFn: async () =>
      (
        await api.put('/notifications/templates', {
          event: editing!.event,
          channel: editing!.channel,
          subject: editing!.subject,
          body,
        })
      ).data,
    onSuccess: () => {
      setEditing(null)
      setError('')
      queryClient.invalidateQueries({ queryKey: ['templates'] })
    },
    onError: (err) => setError(apiErrorMessage(err)),
  })

  return (
    <Card title="Plantillas por evento y canal" actions={<span className="text-xs text-gray-400">Placeholders: {'{{patientName}} {{serviceName}} {{professionalName}} {{date}} {{time}} {{locationName}}'}</span>}>
      {templates.isLoading ? (
        <Spinner />
      ) : (
        <div className="space-y-2">
          {(templates.data ?? []).map((t) => (
            <div key={t.id} className="rounded-lg border border-gray-200 p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm font-medium text-ink">
                  {EVENT_LABELS[t.event] ?? t.event} <Badge className="ml-1">{t.channel}</Badge>
                </p>
                <Button variant="ghost" onClick={() => { setEditing(t); setBody(t.body) }}>Editar</Button>
              </div>
              {editing?.id === t.id ? (
                <div className="mt-2 space-y-2">
                  <Textarea rows={3} value={body} onChange={(e) => setBody(e.target.value)} />
                  <div className="flex gap-2">
                    <Button disabled={save.isPending} onClick={() => save.mutate()}>Guardar</Button>
                    <Button variant="ghost" onClick={() => setEditing(null)}>Cancelar</Button>
                  </div>
                  <ErrorText>{error}</ErrorText>
                </div>
              ) : (
                <p className="mt-1 text-sm text-gray-600">{t.body}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  )
}

// ---------- Usuarios ----------

function UsuariosTab() {
  const queryClient = useQueryClient()
  const [error, setError] = useState('')
  const [form, setForm] = useState({ email: '', password: '', firstName: '', lastName: '', role: 'RECEPTION' })

  const users = useQuery({
    queryKey: ['users'],
    queryFn: async () => (await api.get<UserAccount[]>('/users')).data,
  })

  const create = useMutation({
    mutationFn: async () => (await api.post('/users', form)).data,
    onSuccess: () => {
      setForm({ email: '', password: '', firstName: '', lastName: '', role: 'RECEPTION' })
      setError('')
      queryClient.invalidateQueries({ queryKey: ['users'] })
    },
    onError: (err) => setError(apiErrorMessage(err)),
  })

  const deactivate = useMutation({
    mutationFn: async (id: string) => (await api.delete(`/users/${id}`)).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['users'] }),
  })

  return (
    <div className="space-y-4">
      <Card title="Cuentas internas">
        {users.isLoading ? (
          <Spinner />
        ) : (
          <Table headers={['Nombre', 'Email', 'Rol', 'Estado', '']}>
            {(users.data ?? []).map((u) => (
              <tr key={u.id}>
                <td className="px-2 py-2 font-medium">{u.firstName} {u.lastName}</td>
                <td className="px-2 py-2 text-gray-600">{u.email}</td>
                <td className="px-2 py-2"><Badge>{USER_ROLE_LABELS[u.role]}</Badge></td>
                <td className="px-2 py-2">
                  <Badge className={u.isActive ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-200 text-gray-600'}>
                    {u.isActive ? 'Activa' : 'Desactivada'}
                  </Badge>
                </td>
                <td className="px-2 py-2">
                  {u.isActive && (
                    <Button variant="ghost" onClick={() => deactivate.mutate(u.id)}>Desactivar</Button>
                  )}
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Card>
      <Card title="Crear cuenta interna">
        <div className="grid items-end gap-3 sm:grid-cols-3 lg:grid-cols-6">
          <Field label="Nombres"><Input value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} /></Field>
          <Field label="Apellidos"><Input value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} /></Field>
          <Field label="Email"><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
          <Field label="Contraseña" hint="mín. 8 caracteres"><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></Field>
          <Field label="Rol">
            <Select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
              {['ADMIN', 'DOCTOR', 'RECEPTION', 'ACCOUNTANT'].map((r) => (
                <option key={r} value={r}>{USER_ROLE_LABELS[r]}</option>
              ))}
            </Select>
          </Field>
          <Button
            disabled={!form.email || form.password.length < 8 || !form.firstName || create.isPending}
            onClick={() => create.mutate()}
          >
            Crear
          </Button>
        </div>
        <div className="mt-2"><ErrorText>{error}</ErrorText></div>
      </Card>
    </div>
  )
}
